import Phaser from 'phaser';
import {
  BONUS_COIN_AFTER,
  COIN_EUR,
  SPECIAL_COINS,
  SUBSIDY_CHECKPOINTS_EUR,
  TOTAL_COINS,
  bonusDueEur,
  scoreOf,
  subsidyEur,
  type SpecialCoinId,
} from '../../../shared/scoring.js';
import { TUNING } from '../config/tuning.js';
import { emptyPlan, spreadCoins, type CoinPlan } from '../core/coinPlan.js';
import { agingAt } from '../core/life.js';
import { playfieldArea, playfieldFloorY, type PlayfieldOptions, type Rect } from '../core/playfield.js';
import { TypedEmitter } from '../core/TypedEmitter.js';
import { WorldScale } from '../core/WorldScale.js';
import { BonusField } from '../objects/BonusField.js';
import { Bird } from '../objects/Bird.js';
import { CheckpointBanner } from '../objects/CheckpointBanner.js';
import { CoinField } from '../objects/CoinField.js';
import { PipeField, type CourseSection } from '../objects/PipeField.js';
import { Scenery } from '../objects/Scenery.js';
import { Clouds } from '../objects/Clouds.js';
import { EndScreen, type EndingContent, type EndingStep } from '../ui/EndScreen.js';
import { RunHud } from '../ui/RunHud.js';
import { CSS, PALETTE, TYPE, UI_FONT, UI_HEAVY, formatEuro } from '../ui/style.js';
import { SceneKey } from './keys.js';

/**
 * - ATTRACT: nobody plays – the figure hovers, no gates
 * - DEMO:    nobody plays – the figure plays by itself (preview on the facade), no results
 * - READY:   a run is prepared – waits for the first flap (or `startRun()`)
 * - RUNNING: one life: gates, coins, bonus coins, the figure ages until the life time is over
 *            (on the facade it begins with "Spiel beginnt" and a countdown)
 * - OVER:    run finished (life over, or ended by the edition) – world stands still
 */
export type PlayState = 'ATTRACT' | 'DEMO' | 'READY' | 'RUNNING' | 'OVER';

/** `time` = the life is over; from the edition: `manual` (debug end), `aborted` (round aborted). */
export type RunEndReason = 'time' | 'manual' | 'aborted';

export interface RunResult {
  reason: RunEndReason;
  /** gold coins at the end (collected minus lost by hits) – the score */
  coins: number;
  /** AV depot: own contribution (€) */
  ownEur: number;
  /** state subsidy (€): Grundzulage + special coins */
  subsidyEur: number;
  totalEur: number;
  /** special coins collected */
  specials: SpecialCoinId[];
  /** all coins of the round (special coins too), none lost */
  max: boolean;
  /** gates passed */
  gates: number;
  /** pipes hit (each cost a coin) */
  hits: number;
  flaps: number;
  /** life progress (0…1) when the run ended – 1 = reached retirement */
  progress: number;
  /** time from the start of the run to the end (0 if the run never started) */
  durationMs: number;
  seed: string;
}

export interface RunOptions {
  /** length of one life (= round) in ms – the figure is old when it is over */
  lifeMs: number;
  /** seed for gates and coins, default: random */
  seed?: string;
}

export interface StartOptions {
  /** the figure waits this long (s) before the countdown */
  introSec?: number;
  /** 3…2…1 before the life starts – the figure can fly through everything meanwhile */
  countdown?: boolean;
}

export type PlaySignals = {
  stateChange: [state: PlayState, previous: PlayState];
  runStart: [];
  /** the life (clock, aging) starts – after intro and countdown */
  lifeStart: [];
  /** countdown step: 3, 2, 1, then 0 = go */
  countdown: [step: number];
  flap: [];
  gate: [gates: number];
  coins: [coins: number];
  subsidy: [eur: number];
  /** a special coin was collected */
  special: [id: SpecialCoinId];
  /** a Grundzulage checkpoint (180 / 360 / 540 €) was reached */
  checkpoint: [eur: number];
  /** a pipe was hit – `coins` = what is left */
  hit: [coins: number];
  runEnd: [result: RunResult];
  /** the end screen shows its next step (see `setEnding`) */
  ending: [step: EndingStep];
};

const DEPTH = { sky: 0, clouds: 2, skyline: 5, pipes: 10, coins: 12, ground: 20, bonus: 28, banner: 29, bird: 30, coinFx: 31, hud: 40, overlay: 50, ending: 55 } as const;
/** larger frame gaps (tab switch, GC pause) are clamped so nothing tunnels through a pipe */
const MAX_STEP_SEC = 1 / 20;
const VISIBLE_MASK_KEY = 'play-visible-mask';
const COVER_MASK_KEY = 'play-cover-mask';
const DEMO_LIFE_MS = 60_000;

/**
 * The actual game, identical in all editions. It knows nothing about networking,
 * queues or rounds – editions drive it through the public commands
 * (`showAttract`, `prepareRun`, `startRun`, `flap`, `finishRun`) and listen to
 * `signals`. A run is one life of `lifeMs`; it ends by itself when the life time is up.
 *
 * Scoring (shared/scoring.ts): a round holds exactly `TOTAL_COINS` gold coins, spread over the life
 * (core/coinPlan.ts). Every 2nd collected gold coin (from 360 € every 4th) earns a bonus coin (Grundzulage):
 * it appears in the next stretch not in view yet and is pulled to the figure by itself. A hit costs one coin.
 * The special coins (Starter-Boost, Baby-Bonus) come once a life each at their age (TUNING.special) and are
 * collected like gold coins; they add to the subsidy, a hit does not cost them.
 * At 180 / 360 / 540 € Grundzulage a plane with a banner crosses the playfield – no coins come meanwhile (the
 * plan keeps room for it).
 *
 * By default the game fills the canvas with its own scenery and HUD. `PlayfieldOptions` put it
 * into a part of the canvas instead (e.g. the window of a painted mural, see core/playfield.ts).
 */
export class PlayScene extends Phaser.Scene {
  readonly signals = new TypedEmitter<PlaySignals>();

  private ws!: WorldScale;
  private scenery: Scenery | null = null;
  private clouds!: Clouds;
  private pipes!: PipeField;
  private coinField!: CoinField;
  private bonus!: BonusField;
  private banner!: CheckpointBanner;
  private bird!: Bird;
  private hud: RunHud | null = null;
  private endScreen!: EndScreen;
  private ending: EndingContent | null = null;
  private overlay!: Phaser.GameObjects.Container;
  /** camera mask (WebGL with `playfield.visible`) – switches to `playfield.cover` while a full overlay shows */
  private mask: Phaser.Filters.Mask | null = null;
  private maskCovers = false;
  private overlayText!: Phaser.GameObjects.Text;
  private countText!: Phaser.GameObjects.Text;
  /** maximum width of the overlay text (px) */
  private overlayFit = 0;
  private groundY = 0;
  private birdX = 0;
  private birdStartY = 0;
  private viewWidth = 0;
  /** seconds until a gate rolled now reaches the bird (gates adapt to the age at arrival) */
  private leadSec = 0;
  /** seconds from a coin coming into view (right edge) until it reaches the bird */
  private coinLeadSec = 0;
  /** when the gold coins of the current life come; `nextCoin` = the first one not in the course yet */
  private plan: CoinPlan = emptyPlan(TOTAL_COINS, 1);
  private nextCoin = 0;
  /** next entry of BONUS_COIN_AFTER – a bonus coin is earned when the gold coins reach it */
  private nextBonus = 0;
  /** checkpoints whose bonus coin is on its way – the coins behind it make room for the plane */
  private readonly claimed = new Set<number>();
  /** … and of those the ones whose bonus coin waits for a stretch (their room is still kept) */
  private readonly placing = new Set<number>();
  /** quiet stretches for planes (life time): no gold coin from `from` on before `until`, no bonus coin earned meanwhile */
  private calms: { from: number; until: number }[] = [];
  /** checkpoints the plan keeps room for */
  private reserved = 0;
  /** when the special coins of the current life come (life time), the ones not in the course yet */
  private specialsDue: { id: SpecialCoinId; at: number }[] = [];
  /** special coins collected in this life */
  private specialsGot: SpecialCoinId[] = [];
  /** a checkpoint was reached – its plane starts when the coins in view have passed */
  private pendingBanner: { label: string; at: number } | null = null;

  private _state: PlayState = 'ATTRACT';
  private _coins = 0;
  /** subsidy brought by the bonus coins so far (€) */
  private creditedEur = 0;
  private _gates = 0;
  private _hits = 0;
  private _flaps = 0;
  private reached = new Set<number>();
  private lifeSec = 0;
  private lifeTotalSec = 1;
  private introSec = 0;
  private countSec = 0;
  private invulnerableSec = 0;
  private lastFlapAt = 0;
  private demoRound = 0;
  private seed = '';
  private runStartedAt = 0;
  private created = false;

  constructor(private readonly playfield: PlayfieldOptions = {}) {
    super(SceneKey.Play);
  }

  get state(): PlayState {
    return this._state;
  }

  /** gold coins now (collected minus lost) */
  get coins(): number {
    return this._coins;
  }

  /** AV depot: own contribution (€) */
  get ownEur(): number {
    return this._coins * COIN_EUR;
  }

  /** state subsidy credited so far (€): the bonus coins that have arrived (Grundzulage) + the special coins */
  get subsidyEur(): number {
    return this.creditedEur + this.specialEur;
  }

  /** special coins collected so far */
  get specials(): readonly SpecialCoinId[] {
    return this.specialsGot;
  }

  private get specialEur(): number {
    return SPECIAL_COINS.reduce((sum, c) => sum + (this.specialsGot.includes(c.id) ? c.eur : 0), 0);
  }

  get gates(): number {
    return this._gates;
  }

  get hits(): number {
    return this._hits;
  }

  get flaps(): number {
    return this._flaps;
  }

  /** a dark full overlay covers the window: "Spiel beginnt" or the end screen (result → call to action) */
  get covered(): boolean {
    return this.created && (this.overlay.visible || this.endScreen.shown);
  }

  /** "Spiel beginnt" or the countdown is running (the life has not started yet) */
  get inCountdown(): boolean {
    return this._state === 'RUNNING' && this.introSec + this.countSec > 0;
  }

  /** 0 = start of life … 1 = retirement */
  get lifeProgress(): number {
    return Math.min(1, this.lifeSec / this.lifeTotalSec);
  }

  /** seconds left in the current life (the full length before the run starts) */
  get lifeRemainingSec(): number {
    return Math.max(0, this.lifeTotalSec - this.lifeSec);
  }

  /** world → pixel conversion of the running game (available after create) */
  get worldScale(): WorldScale {
    return this.ws;
  }

  // ---------------------------------------------------------------------------
  create(): void {
    const pf = this.playfield;
    const area = playfieldArea(pf, this.scale.width, this.scale.height);
    const { width, height } = area;
    this.viewWidth = width;
    // the camera renders the playfield into its area of the canvas: (0, 0) = top left of the area
    this.cameras.main.setViewport(area.x, area.y, width, height);
    this.maskVisibleParts(width, height);
    this.ws = new WorldScale(width, height, pf.worldHeight);
    this.groundY = playfieldFloorY(pf, this.ws);

    if (pf.scenery !== false) {
      this.scenery = new Scenery(this, this.ws, width, height, this.groundY, { sky: DEPTH.sky, skyline: DEPTH.skyline, ground: DEPTH.ground }, pf.skyline);
    }
    this.clouds = new Clouds(this, this.ws, width, DEPTH.clouds);
    this.coinField = new CoinField(this, this.ws, this.groundY, pf.coinsMinY ?? TUNING.coins.minY, DEPTH.coins, width, DEPTH.coinFx);
    this.pipes = new PipeField(this, this.ws, width, this.groundY, DEPTH.pipes, (s) => this.onSection(s), pf.pipeSide);
    this.bonus = new BonusField(this, this.ws, DEPTH.bonus, (x, y, eur) => this.onBonus(x, y, eur));
    this.banner = new CheckpointBanner(this, this.ws, width, DEPTH.banner);

    const B = TUNING.bird;
    this.birdX = Math.min(width * (pf.birdX ?? B.xFraction), this.ws.px(B.maxX));
    this.bird = new Bird(this, this.ws).setDepth(DEPTH.bird);
    this.birdStartY = this.groundY * B.startYFraction;
    // a gate is rolled one spacing before it spawns at the right edge
    this.leadSec = (this.ws.wu(width - this.birdX) + TUNING.pipes.spacing) / TUNING.world.scrollSpeed;
    this.coinLeadSec = (this.ws.wu(width - this.birdX) + TUNING.coins.displaySize) / TUNING.world.scrollSpeed;

    if (pf.hud !== false) this.hud = new RunHud(this, this.ws, width, DEPTH.hud);
    this.createOverlay(width, height);
    this.endScreen = new EndScreen(this, this.textUnit(width, height), width, height, this.textPart(width, height), DEPTH.ending, (step) =>
      this.signals.emit('ending', step),
    );

    this.created = true;
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.created = false;
    });
    this.enterAttract(false);
  }

  // ---------------------------------------------------------------------------
  // Commands (called by the edition)
  // ---------------------------------------------------------------------------

  /** Nobody plays: the figure hovers – or plays by itself (`demo`). Ends a running run without a result. */
  showAttract(demo = false): void {
    if (!this.guard('showAttract')) return;
    if (demo && this._state === 'DEMO') return;
    this.enterAttract(demo);
  }

  /**
   * What the end of a round says (result → story → call to action, over the whole window). Without it the
   * world just stands still at the end and the edition shows the result itself.
   */
  setEnding(content: EndingContent | null): void {
    this.ending = content;
  }

  /** the step the end screen shows, null = none */
  get endingStep(): EndingStep | null {
    return this.endScreen?.step ?? null;
  }

  /** Jump to the next step of the end screen (a tap on the phone). */
  skipEnding(): void {
    this.endScreen?.skip();
  }

  /** Prepare a fresh life; it starts with the first flap (or `startRun()`). */
  prepareRun(options: RunOptions): void {
    if (!this.guard('prepareRun')) return;
    this.resetRun(options.seed ?? Date.now().toString(36), options.lifeMs);
    this.setState('READY');
  }

  /** Start the prepared run without a flap – optionally with a short wait (intro) and the countdown. */
  startRun(options: StartOptions = {}): boolean {
    if (!this.created || this._state !== 'READY') return false;
    this.introSec = Math.max(0, options.introSec ?? 0);
    this.countSec = options.countdown ? TUNING.countdown.steps : 0;
    this.planCoins(this.countSec);
    // with an intro the figure first waits in the window (no text), then 3…2…1
    if (this.introSec === 0) {
      if (this.countSec > 0) this.startCountdown();
      else this.bird.launch();
    }
    this.begin();
    if (this.introSec + this.countSec === 0) this.signals.emit('lifeStart');
    return true;
  }

  /** Flap. In READY this starts the run. Returns false if the flap was ignored. */
  flap(): boolean {
    if (!this.created) return false;
    if (this._state === 'READY') {
      this.begin();
      this.signals.emit('lifeStart');
    } else if (this._state !== 'RUNNING' || this.introSec > 0) return false;
    this.doFlap();
    this.signals.emit('flap');
    return true;
  }

  /** End the current run from outside (time limit, manual, aborted). No-op if no run is active. */
  finishRun(reason: RunEndReason): void {
    if (!this.created || (this._state !== 'READY' && this._state !== 'RUNNING')) return;
    if (reason !== 'time') this.bird.freeze();
    this.endRun(reason);
  }

  // ---------------------------------------------------------------------------
  override update(_time: number, deltaMs: number): void {
    const dt = Math.min(deltaMs / 1000, MAX_STEP_SEC);
    const floorY = this.groundY - this.bird.footOffset;
    this.syncMask();

    switch (this._state) {
      case 'ATTRACT':
      case 'READY':
        this.scrollScenery(this.ws.px(TUNING.world.scrollSpeed * dt));
        this.bird.bob(dt);
        break;

      case 'DEMO':
        this.autopilot();
        this.stepRun(dt, floorY);
        break;

      case 'RUNNING':
        if (this.introSec > 0) {
          // the world waits
          this.introSec = Math.max(0, this.introSec - dt);
          this.bird.bob(dt);
          if (this.introSec === 0) this.countSec > 0 ? this.startCountdown() : this.startLife();
          break;
        }
        this.stepRun(dt, floorY);
        break;

      case 'OVER':
        // the player drops to the ground (freeze() stops it for other reasons)
        this.bird.step(dt, floorY);
        this.endScreen.update(dt);
        break;
    }
  }

  /** One frame of a life (also the demo and the countdown before the life). */
  private stepRun(dt: number, floorY: number): void {
    const dx = this.ws.px(TUNING.world.scrollSpeed * dt);
    if (this.countSec > 0) {
      const before = Math.ceil(this.countSec);
      this.countSec = Math.max(0, this.countSec - dt);
      const step = Math.ceil(this.countSec);
      if (step !== before) {
        this.showCount(step);
        if (step === 0) this.startLife();
      }
    } else {
      this.lifeSec += dt;
      this.applyLifeProgress();
      this.pipes.setAging(agingAt((this.lifeSec + this.leadSec) / this.lifeTotalSec));
    }

    this.scrollScenery(dx);
    this.pipes.scroll(dx);
    this.coinField.scroll(dx, dt);
    this.placeCoins();
    this.bird.step(dt, floorY);
    const magnet = Math.min(this.ws.px(TUNING.bonus.magnetDx), (this.viewWidth - this.birdX) * TUNING.bonus.magnetShare);
    this.coinField.pullBonus(this.bird.x, this.bird.hitRadius, magnet, (x, y, eur) => this.bonus.attract(x, y, eur));
    this.bonus.update(dt, this.bird.x, this.bird.y, this.bird.hitRadius);
    if (this.pendingBanner && this.lifeSec >= this.pendingBanner.at) {
      this.banner.show(this.pendingBanner.label);
      this.pendingBanner = null;
    }

    const passed = this.pipes.collectPassed(this.bird.x);
    if (passed) {
      this._gates += passed;
      if (this._state === 'RUNNING') this.signals.emit('gate', this._gates);
    }
    const spots = this.coinField.collect(this.bird.x, this.bird.y, this.bird.hitRadius);
    for (const spot of spots) spot.special ? this.onSpecial(spot.special, spot.x, spot.y) : this.onCoin();
    // coins missed: a checkpoint out of reach (or overdue) needs no room any more
    if (this.openCheckpoints().length < this.reserved) this.replan(this.replanStart());
    this.checkBonus();

    // the countdown and the demo fly through everything
    this.invulnerableSec = Math.max(0, this.invulnerableSec - dt);
    const safe = this._state === 'DEMO' || this.countSec > 0 || this.invulnerableSec > 0;
    if (!safe && this.bird.anyHit((x, y, r) => this.pipes.hits(x, y, r))) this.onHit();

    if (this.lifeSec >= this.lifeTotalSec) {
      if (this._state === 'DEMO') this.startDemo();
      else this.endRun('time');
    }
  }

  // ---------------------------------------------------------------------------

  private enterAttract(demo: boolean): void {
    this.hideOverlay();
    if (demo) {
      this.startDemo();
      return;
    }
    this.resetRun('attract', DEMO_LIFE_MS);
    this.hud?.setVisible(false);
    this.setState('ATTRACT');
  }

  /** a new demo life, the figure plays by itself */
  private startDemo(): void {
    this.resetRun(`demo-${++this.demoRound}`, DEMO_LIFE_MS);
    this.bird.launch();
    this.setState('DEMO');
  }

  private resetRun(seed: string, lifeMs: number): void {
    this.seed = seed;
    this.lifeTotalSec = Math.max(1, lifeMs / 1000);
    this.lifeSec = 0;
    this._coins = 0;
    this.creditedEur = 0;
    this.specialsGot = [];
    this.pendingBanner = null;
    this._gates = 0;
    this._hits = 0;
    this._flaps = 0;
    this.reached.clear();
    this.introSec = 0;
    this.countSec = 0;
    this.invulnerableSec = 0;
    this.hideOverlay();
    this.endScreen.hide();
    this.banner.hide();
    this.bonus.reset();
    // coins first: resetting the pipes announces the first stretch
    this.coinField.reset(seed);
    // after the reset of coins and checkpoints: all checkpoints are open again
    this.planCoins(0);
    this.pipes.reset(seed, this.birdStartY);
    this.bird.reset(this.birdX, this.birdStartY);
    this.hud?.setDepot(0);
    this.hud?.setSubsidy(0);
    this.hud?.setProgress(0);
    this.hud?.setVisible(true);
  }

  /** background layers (own scenery, clouds) by `dx` pixels of the world's scroll */
  private scrollScenery(dx: number): void {
    this.scenery?.scroll(dx);
    this.clouds.scroll(dx);
  }

  private begin(): void {
    this.runStartedAt = performance.now();
    this.setState('RUNNING');
    this.signals.emit('runStart');
  }

  private startCountdown(): void {
    this.hideOverlay();
    this.bird.launch();
    this.showCount(Math.ceil(this.countSec));
  }

  private startLife(): void {
    // "LOS!" of the countdown stays a moment longer (it fades out by itself)
    this.hideOverlay(false);
    this.bird.launch();
    this.signals.emit('lifeStart');
  }

  private doFlap(): void {
    this.bird.flap();
    this._flaps++;
    this.lastFlapAt = this.time.now;
  }

  /** The gold coins of a new life. The first one can only come once it has scrolled in from the right edge. */
  private planCoins(countdownSec: number): void {
    const C = TUNING.coins;
    this.plan = emptyPlan(TOTAL_COINS, C.minGapSec);
    this.nextCoin = 0;
    this.nextBonus = 0;
    this.claimed.clear();
    this.placing.clear();
    this.calms = [];
    this.replan(Math.max(C.firstSec, this.coinLeadSec - countdownSec + 0.2));
    const L = TUNING.life;
    this.specialsDue = SPECIAL_COINS.map((c) => ({ id: c.id, at: ((TUNING.special.atAge[c.id] - L.startAge) / (L.endAge - L.startAge)) * this.lifeTotalSec }));
    this.specialsDue.sort((a, b) => a.at - b.at);
  }

  /** room for a plane in the middle of the life: flight + until new coins come into view */
  private get calmSec(): number {
    const K = TUNING.checkpoint;
    return K.bannerSec * K.coinsBackShare + this.coinLeadSec + K.calmExtraSec + 0.5;
  }

  /** room after the last gold coin when the maximum can still be reached: its bonus coin comes, then the plane */
  private get finalSec(): number {
    return this.coinLeadSec + TUNING.pipes.spacing / TUNING.world.scrollSpeed + TUNING.checkpoint.bannerSec + 0.3;
  }

  /**
   * Spread the gold coins still to come evenly over the rest of the life, keeping room for the planes of the
   * checkpoints still open – again whenever one is reached, gets out of reach or is overdue. What would arrive
   * during a quiet stretch comes after it.
   */
  private replan(start: number): void {
    const open = this.openCheckpoints();
    this.reserved = open.length;
    const max = SUBSIDY_CHECKPOINTS_EUR[SUBSIDY_CHECKPOINTS_EUR.length - 1];
    const room = open.reduce((sum, level) => sum + (level === max ? this.finalSec : this.calmSec), 0);
    const end = this.lifeTotalSec - (open.includes(max) ? 0 : TUNING.coins.lastSecBeforeEnd) - room;
    spreadCoins(this.plan, this.nextCoin, start, Math.max(start, end));
    this.calms = this.calms.filter((c) => c.until > this.lifeSec);
    for (const c of this.calms) {
      let i = this.nextCoin;
      while (i < this.plan.at.length && this.plan.at[i] < c.from) i++;
      if (i < this.plan.at.length && this.plan.at[i] < c.until) spreadCoins(this.plan, i, c.until, Math.max(c.until, end));
    }
  }

  private replanStart(): number {
    const before = this.plan.at[this.nextCoin - 1] ?? 0;
    return Math.max(this.lifeSec + this.coinLeadSec, before + this.plan.gap);
  }

  /**
   * Checkpoints the plan keeps room for: not reached, no bonus coin on its way, still in reach with the gold
   * coins to come – and not overdue (the gold coin that earns it when nothing is missed has passed).
   */
  private openCheckpoints(): number[] {
    const ahead = this.coinField?.goldAhead(this.bird.x) ?? 0;
    const passed = this.nextCoin - ahead;
    const best = subsidyEur(this.ownEur + (this.plan.at.length - passed) * COIN_EUR);
    return SUBSIDY_CHECKPOINTS_EUR.filter((level) => {
      if (this.placing.has(level)) return true;
      if (this.reached.has(level) || this.claimed.has(level) || best < level) return false;
      const due = BONUS_COIN_AFTER.find((n) => bonusDueEur(n) >= level) ?? TOTAL_COINS;
      return passed < due;
    });
  }

  /** no bonus coin is earned during a quiet stretch (or while its bonus coin waits) – the ones due come after the plane */
  private get calm(): boolean {
    return this.placing.size > 0 || this.calms.some((c) => c.until > this.lifeSec);
  }

  /** A plane will start around life time `at`: no coin may come into view while it flies. */
  private makeRoom(at: number): void {
    const K = TUNING.checkpoint;
    this.calms.push({ from: at - this.plan.gap / 2, until: at + K.bannerSec * K.coinsBackShare + this.coinLeadSec + K.calmExtraSec });
    this.replan(this.replanStart());
  }

  /** A new stretch between two gates – coins get their place in it when they come. */
  private onSection(s: CourseSection): void {
    this.coinField?.addStretch(s);
  }

  /** Put the gold and special coins into the course just before they come into view. */
  private placeCoins(): void {
    const pxPerSec = this.ws.px(TUNING.world.scrollSpeed);
    const edge = this.viewWidth + this.ws.px(TUNING.coins.displaySize);
    // the countdown is not part of the life, but the world moves meanwhile
    const xAt = (at: number) => this.birdX + (at - this.lifeSec + this.countSec) * pxPerSec;
    while (this.nextCoin < this.plan.at.length) {
      const x = xAt(this.plan.at[this.nextCoin]);
      if (x > edge) break;
      this.coinField.place(Math.max(x, edge - this.ws.px(TUNING.coins.displaySize) / 2));
      this.nextCoin++;
    }
    while (this.specialsDue.length) {
      // no coin comes into view while a plane flies: a special coin due then comes after it
      const due = this.specialsDue[0];
      const calm = this.calms.find((c) => due.at >= c.from && due.at < c.until);
      if (calm) due.at = calm.until;
      const x = xAt(due.at);
      if (x > edge) break;
      this.coinField.placeSpecial(Math.max(x, edge - this.ws.px(TUNING.coins.displaySize) / 2), due.id);
      this.specialsDue.shift();
    }
  }

  private onCoin(): void {
    this._coins++;
    this.bird.react('joy');
    this.hud?.setDepot(this.ownEur);
    this.signals.emit('coins', this._coins);
    this.checkBonus();
  }

  /** A special coin was collected: its subsidy is credited right away (a hit does not cost it). */
  private onSpecial(id: SpecialCoinId, x: number, y: number): void {
    const coin = SPECIAL_COINS.find((c) => c.id === id);
    if (!coin || this.specialsGot.includes(id)) return;
    this.specialsGot.push(id);
    this.bird.react('joy');
    this.hud?.setSubsidy(this.subsidyEur);
    this.coinField.float(`${coin.name}\n+${formatEuro(coin.eur)}`, id === 'baby' ? CSS.baby : CSS.starter, x, y - this.ws.px(110), TUNING.feedback.specialLabelSize, true);
    this.coinField.sparks(x, y, Phaser.Display.Color.HexStringToColor(id === 'baby' ? CSS.baby : CSS.starter).color, true);
    this.signals.emit('special', id);
    this.signals.emit('subsidy', this.subsidyEur);
  }

  /** 1:2 up to 360 € own contribution, then 1:4: a bonus coin for every 2nd / 4th gold coin */
  private checkBonus(): void {
    while (!this.calm && this.nextBonus < BONUS_COIN_AFTER.length && this._coins >= BONUS_COIN_AFTER[this.nextBonus]) {
      this.earnBonus(BONUS_COIN_AFTER[this.nextBonus++]);
    }
  }

  /**
   * The bonus coin for `gold` gold coins: the subsidy due since the last one (50 €, the last one 40 €; one after
   * the other when several came due during a plane). It appears in the next stretch not in view yet; if it reaches
   * a checkpoint, the gold coins behind it make room for the plane.
   */
  private earnBonus(gold: number): void {
    const promised = this.creditedEur + this.bonus.pendingEur + this.coinField.bonusEur;
    const eur = Math.min(subsidyEur(this.ownEur), bonusDueEur(gold)) - promised;
    if (eur <= 0) return;
    const checkpoint = SUBSIDY_CHECKPOINTS_EUR.find((l) => !this.reached.has(l) && !this.claimed.has(l) && promised < l && promised + eur >= l);
    if (checkpoint !== undefined) {
      this.claimed.add(checkpoint);
      this.placing.add(checkpoint);
    }
    this.coinField.spawnBonus(eur, checkpoint, (x) => {
      if (checkpoint === undefined) return;
      this.placing.delete(checkpoint);
      this.makeRoom(this.lifeSec + (x - this.birdX) / this.ws.px(TUNING.world.scrollSpeed));
    });
  }

  /** A bonus coin arrived: its subsidy is credited (less if a hit cost a coin meanwhile). */
  private onBonus(x: number, y: number, eur: number): void {
    const add = Math.min(eur, subsidyEur(this.ownEur) - this.creditedEur);
    if (add <= 0) return;
    this.creditedEur += add;
    const now = this.creditedEur;
    this.hud?.setSubsidy(this.subsidyEur);
    this.signals.emit('subsidy', this.subsidyEur);
    this.coinField.float(`+${formatEuro(add)}`, CSS.bonus, x, y - this.ws.px(80));
    this.coinField.sparks(x, y, PALETTE.blueHi);
    for (const level of SUBSIDY_CHECKPOINTS_EUR) {
      if (now < level || this.reached.has(level)) continue;
      this.reached.add(level);
      if (this._state === 'RUNNING' || this._state === 'DEMO') this.onCheckpoint(level);
    }
  }

  /** Plane with banner – it starts when the coins in view have passed. */
  private onCheckpoint(level: number): void {
    const ahead = this.coinField.lastAhead(this.bird.x);
    const start = this.lifeSec + (ahead === null ? 0 : (ahead - this.bird.x) / this.ws.px(TUNING.world.scrollSpeed));
    // reached without its bonus coin making room (hits changed the sums): room now
    if (!this.claimed.has(level)) this.makeRoom(start);
    const label = this.reached.size === SUBSIDY_CHECKPOINTS_EUR.length ? `${formatEuro(level)} Grundzulage – Maximum!` : `${formatEuro(level)} Grundzulage erreicht!`;
    this.pendingBanner = { label, at: start };
    if (this._state === 'RUNNING') this.signals.emit('checkpoint', level);
  }

  /** A pipe was hit: costs a coin and its share of the subsidy (1:2 or 1:4), right away. The bird blinks and passes through. */
  private onHit(): void {
    this._hits++;
    if (this._coins > 0) {
      const before = subsidyEur(this.ownEur);
      this._coins--;
      // the subsidy that belongs to the lost coin (25 € / 12,50 €) is taken off what was credited; bonus coins still
      // on their way bring what is due – the result stays the subsidy of the coins left
      const lost = Math.min(this.creditedEur, before - subsidyEur(this.ownEur));
      this.creditedEur -= lost;
      // the bonus coins of the lost gold coins are earned again when they are collected again (their planes don't come again)
      const next = BONUS_COIN_AFTER.findIndex((n) => n > this._coins);
      this.nextBonus = Math.min(this.nextBonus, next < 0 ? BONUS_COIN_AFTER.length : next);
      this.coinField.lose(this.bird.x, this.bird.y, lost);
      this.hud?.setDepot(this.ownEur);
      this.hud?.setSubsidy(this.subsidyEur);
      this.signals.emit('coins', this._coins);
      this.signals.emit('subsidy', this.subsidyEur);
    }
    this.cameras.main.shake(160, 0.006);
    this.invulnerableSec = TUNING.hits.invulnerableSec;
    this.bird.blink(TUNING.hits.invulnerableSec);
    this.bird.react('hit');
    this.signals.emit('hit', this._coins);
  }

  /** Demo: flap towards the next coin, close to a gate towards its gap. */
  private autopilot(): void {
    const D = TUNING.demo;
    const r = this.bird.hitRadius;
    const gate = this.pipes.gapAhead(this.bird.x, r);
    const coin = this.coinField.nextAhead(this.bird.x - r);
    let target = gate.centre;
    if (coin && (gate.distance > this.pipes.width * 2 || coin.x < this.bird.x + gate.distance)) target = coin.y;
    if (this.bird.y > target + r * D.tolerance && this.bird.velocityY > -120 && this.time.now - this.lastFlapAt > D.minFlapGapSec * 1000) {
      this.doFlap();
    }
  }

  private applyLifeProgress(): void {
    const p = this.lifeProgress;
    this.bird.setLifeProgress(p);
    this.hud?.setProgress(p);
  }

  private endRun(reason: RunEndReason): void {
    const durationMs = this._state === 'RUNNING' ? performance.now() - this.runStartedAt : 0;
    if (reason === 'time') {
      // the edition's clock may run out a moment before ours – the life is over either way
      this.lifeSec = this.lifeTotalSec;
      this.applyLifeProgress();
      // a full life: the fox cheers when it has landed
      this.bird.cheer();
    }
    this.setState('OVER');
    this.pendingBanner = null;
    // what is still on its way or not brought yet is credited – the result matches the coins
    this.bonus.arriveAll(this.bird.x, this.bird.y);
    this.creditedEur = subsidyEur(this.ownEur);
    this.hud?.setSubsidy(this.subsidyEur);
    this.banner.hide();
    this.hideOverlay();
    const score = scoreOf(this._coins, this.specialsGot);
    if (this.ending && reason !== 'aborted') this.endScreen.start(this.ending, { ...score, max: score.max && this._hits === 0 });
    this.signals.emit('runEnd', {
      reason,
      coins: score.coins,
      ownEur: score.ownEur,
      subsidyEur: score.subsidyEur,
      totalEur: score.totalEur,
      specials: score.specials,
      max: score.max && this._hits === 0,
      gates: this._gates,
      hits: this._hits,
      flaps: this._flaps,
      progress: this.lifeProgress,
      durationMs,
      seed: this.seed,
    });
  }

  // --- "Spiel beginnt" and countdown, over the whole playfield -------------------------

  private createOverlay(width: number, height: number): void {
    const px = (wu: number) => Math.round(this.ws.px(wu));
    const bg = this.add.rectangle(0, 0, width, height, PALETTE.ink, 0.92).setOrigin(0, 0);
    const main = this.textPart(width, height);
    this.overlayFit = main.width * 0.9;
    // as large as the headlines of the end screen
    const title = Math.round(TYPE.title * this.textUnit(width, height));
    this.overlayText = this.add
      .text(main.x + main.width / 2, main.y + main.height / 2, '', { fontFamily: UI_FONT, fontSize: `${title}px`, fontStyle: UI_HEAVY, color: CSS.orange, stroke: CSS.ink, strokeThickness: Math.round(title * 0.12) })
      .setOrigin(0.5);
    this.overlay = this.add.container(0, 0, [bg, this.overlayText]).setDepth(DEPTH.overlay).setVisible(false);
    this.countText = this.add
      .text(main.x + main.width / 2, main.y + main.height * 0.45, '', { fontFamily: UI_FONT, fontSize: `${px(220)}px`, fontStyle: UI_HEAVY, color: CSS.text, stroke: CSS.ink, strokeThickness: px(18) })
      .setOrigin(0.5)
      .setDepth(DEPTH.overlay)
      .setVisible(false);
  }

  /**
   * Pixels per TYPE unit of the full overlays. In a painted window (facade) 1/1000 of the width of its
   * largest visible part – like the texts of the facade around the game; otherwise also at most a world unit,
   * so a wide screen does not get huge letters.
   */
  private textUnit(width: number, height: number): number {
    const main = this.mainPart(width, height);
    const unit = this.playfield.visible?.length ? main.width / 1000 : Math.min(this.ws.px(1), main.width / 1000);
    return unit * (this.playfield.textScale ?? 1);
  }

  /** where the texts of the full overlays stand: `playfield.textArea`, else the largest visible part */
  private textPart(width: number, height: number): Rect {
    return this.playfield.textArea ?? this.mainPart(width, height);
  }

  /** the largest visible part of the window – texts go there (the rest may be cut off by the mask) */
  private mainPart(width: number, height: number): Rect {
    const parts = this.playfield.visible?.length ? this.playfield.visible : [{ x: 0, y: 0, width, height }];
    return parts.reduce((a, b) => (b.width * b.height > a.width * a.height ? b : a));
  }

  private showOverlay(text: string): void {
    this.overlayText.setText(text).setScale(1);
    const fit = Math.min(1, this.overlayFit / this.overlayText.width);
    this.overlayText.setScale(fit * 0.6);
    this.overlay.setVisible(true).setAlpha(0);
    this.tweens.add({ targets: this.overlay, alpha: 1, duration: 200 });
    this.tweens.add({ targets: this.overlayText, scale: fit, duration: 420, ease: 'Back.Out' });
  }

  private hideOverlay(withCount = true): void {
    if (!this.overlay) return;
    this.tweens.killTweensOf(this.overlay);
    this.overlay.setVisible(false);
    if (!withCount || !this.countText.visible) return;
    this.tweens.killTweensOf(this.countText);
    this.countText.setVisible(false);
  }

  /** 3, 2, 1 – then "LOS!" fades out */
  private showCount(step: number): void {
    this.signals.emit('countdown', step);
    this.tweens.killTweensOf(this.countText);
    this.countText.setText(step > 0 ? String(step) : 'LOS!').setColor(step > 0 ? CSS.text : CSS.orange).setVisible(true).setAlpha(1).setScale(1.5);
    this.tweens.add({ targets: this.countText, scale: 1, duration: 300, ease: 'Back.Out' });
    if (step === 0) {
      this.tweens.add({ targets: this.countText, alpha: 0, delay: 450, duration: 300, onComplete: () => this.countText.setVisible(false) });
    }
  }

  /**
   * Cut the game off outside `playfield.visible` (window with stepped edges). A static texture
   * mask on the camera: it stays in place while the camera shakes. WebGL only – the Canvas
   * renderer just clips to the area.
   */
  private maskVisibleParts(width: number, height: number): void {
    const { visible: parts, cover } = this.playfield;
    if (!parts?.length || this.renderer.type !== Phaser.WEBGL) return;
    const paint = (key: string, draw: (ctx: CanvasRenderingContext2D) => void): boolean => {
      if (this.textures.exists(key)) this.textures.remove(key);
      const texture = this.textures.createCanvas(key, Math.ceil(width), Math.ceil(height));
      if (!texture) return false;
      const ctx = texture.getContext();
      ctx.fillStyle = '#ffffff';
      draw(ctx);
      texture.refresh();
      return true;
    };
    if (!paint(VISIBLE_MASK_KEY, (ctx) => parts.forEach((r) => ctx.fillRect(r.x, r.y, r.width, r.height)))) return;
    this.mask = this.cameras.main.filters.internal.addMask(VISIBLE_MASK_KEY);
    this.maskCovers = false;
    if (!cover?.length) return;
    paint(COVER_MASK_KEY, (ctx) => {
      ctx.beginPath();
      for (const poly of cover) {
        poly.forEach((p, i) => (i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y)));
        ctx.closePath();
      }
      ctx.fill();
    });
  }

  /**
   * "Spiel beginnt" and the end screen cover the whole window opening (`playfield.cover`, with the reveal
   * faces of its stepped edges), the game itself only the visible parts.
   */
  private syncMask(): void {
    if (!this.mask || !this.playfield.cover?.length) return;
    const covers = this.covered;
    if (covers === this.maskCovers) return;
    this.maskCovers = covers;
    this.mask.setTexture(covers ? COVER_MASK_KEY : VISIBLE_MASK_KEY);
  }

  private setState(next: PlayState): void {
    const prev = this._state;
    if (prev === next) return;
    this._state = next;
    this.signals.emit('stateChange', next, prev);
  }

  private guard(cmd: string): boolean {
    if (!this.created) console.warn(`PlayScene.${cmd}() called before the scene was created – wait for createGame().ready`);
    return this.created;
  }
}
